import { useCallback, useEffect, useRef, useState } from "react";
import { useRouter } from "next/router";
import { useIntersection } from "react-use";

import Spinner from "components/spinner/spinner";
import { ArticleListCard } from "components/articleListCard/articleListCard";

import {
  MixpanelProperties,
  useMixpanelTracking,
} from "lib/hooks/useMixpanelTracking";
import type {
  ArticleList,
  ArticleListRequestQuery,
  ArticleShort,
} from "lib/types/article";
import { getArticlesByPage } from "lib/fetchers/articles";

type LazyLoadArticleListProps = {
  query: ArticleListRequestQuery;
  data: ArticleList;
  withPills?: boolean;
};

const LazyLoadArticleList = ({
  query,
  data,
  withPills = true,
}: LazyLoadArticleListProps) => {
  const { track } = useMixpanelTracking();
  const router = useRouter();
  const intersectionRef = useRef<HTMLLIElement | null>(null);
  const [queryParams, setQueryParams] =
    useState<ArticleListRequestQuery>(query);
  const [articles, setArticles] = useState<ArticleShort[]>(data.articles || []);
  const [isLoading, setIsLoading] = useState(false);
  const intersection = useIntersection(intersectionRef, {
    root: null,
    rootMargin: "0px",
    threshold: 0.1,
  });

  const trackMixpanelEvent = async (
    title: string,
    properties: MixpanelProperties
  ) => {
    await track(title, {
      ...properties,
      "Page path": router.asPath,
    });
  };

  const fetchNewPage = useCallback(async () => {
    const data = await getArticlesByPage(queryParams);

    setIsLoading(false);

    if (data.data && data.data.articles && data.data.articles.length > 0) {
      setArticles((prevState) => {
        return [...prevState, ...data!.data!.articles];
      });
    }
  }, [queryParams, setIsLoading]);

  useEffect(() => {
    if (
      queryParams.page! <= data.pages &&
      queryParams.page !== data.currentPage
    ) {
      if (!isLoading) {
        setIsLoading(true);
        fetchNewPage();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    queryParams.page,
    fetchNewPage,
    data.pages,
    data.currentPage,
    setIsLoading,
  ]);

  useEffect(() => {
    if (intersection && intersection.isIntersecting && !isLoading) {
      setQueryParams((q) => ({ ...q, page: q.page! + 1 }));
    }
    // eslint-disable-next-line
  }, [intersection, setQueryParams]);

  return (
    <>
      <ul aria-label={`List of articles`}>
        {articles.map((article, index) => {
          const pills =
            withPills && article.sector
              ? [
                  {
                    name: article.sector.name,
                    url: article.sector.url,
                  },
                ]
              : undefined;
          return (
            <li
              ref={index === articles.length - 1 ? intersectionRef : undefined}
              className={
                "m-0 border-b-[1px] first:border-t-[1px] max-md:border-[#5B5B5B]"
              }
              key={article.id}
            >
              <ArticleListCard
                {...article}
                linkLabel={"Article"}
                tagPills={pills}
                onTrackingEventClicked={async ({ title, ...rest }) =>
                  await trackMixpanelEvent(title, rest)
                }
              />
            </li>
          );
        })}
      </ul>
      {isLoading && (
        <div className={"p-4"} data-testid={"lazyLoadArticleList__spinner"}>
          <Spinner colour={"cerise"} />
        </div>
      )}
    </>
  );
};

export default LazyLoadArticleList;
